import React, { useCallback, useContext, useMemo, useState } from "react";
import { Color1Set, Shikii1Set } from "../App";
import JSZip from "jszip";
import { saveAs } from "file-saver";

import {
  buildMetaOverride,
  makeYears,
  makeLegendFolderKml,
  makeNetworkLinkKml,
} from "./kmlCore";

import {
  makeLegendCommonPng,
  makeLegendYearPngsForYears,
} from "./kmlCanvas";

/**
 * v6 方針
 * - build_start.php が即レスポンスを返しつつ、サーバ側で build 継続
 * - フロントは先に KMZ をメモリ上で作る
 * - KMZ 作成完了後、即 status 確認
 * - success なら即ダウンロード
 * - 未完了ならポーリングし、success になったらダウンロード
 */

const API_BASE = "https://kmlv1.meshdata.jp/api/kml";
const BUILD_START_URL = `${API_BASE}/build_start/`;
const BUILD_STATUS_URL = `${API_BASE}/build_status/`;

const fetchBuildStatus = async (id) => {
  const res = await fetch(`${BUILD_STATUS_URL}?id=${encodeURIComponent(id)}`, {
    method: "GET",
    cache: "no-store",
  });

  if (!res.ok) {
    throw new Error(`status request failed: ${res.status}`);
  }

  const json = await res.json();

  if (!json?.ok) {
    throw new Error(json?.message || "status read failed");
  }

  return json;
};

const waitForBuildSuccess = async (
  id,
  {
    timeoutMs = 60000,
    intervalMs = 1000,
    onProgress,
  } = {}
) => {
  const startedAt = Date.now();

  while (Date.now() - startedAt <= timeoutMs) {
    const statusJson = await fetchBuildStatus(id);

    if (typeof onProgress === "function") {
      onProgress(statusJson);
    }

    if (statusJson.status === "success") {
      return statusJson;
    }

    if (statusJson.status === "error") {
      throw new Error(statusJson?.message || "kml build failed");
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  throw new Error("kml build timeout");
};

const Csv2kml_v4 = ({ meta, area, ratio, alpha, children }) => {
  const { color1set } = useContext(Color1Set);
  const { shikii1set } = useContext(Shikii1Set);

  const [isWorking, setIsWorking] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [statusText, setStatusText] = useState("");
  const [progress, setProgress] = useState(null);
  const [buildId, setBuildId] = useState("");
  const [errorText, setErrorText] = useState("");

  const phaseLabel = useMemo(() => {
    switch (phase) {
      case "starting":
        return "生成開始を依頼しています…";
      case "building-kmz":
        return "KMZを作成しています…";
      case "checking":
        return "生成状況を確認しています…";
      case "polling":
        return "サーバ側でKML生成中です…";
      case "done":
        return "ダウンロードを開始しました。";
      case "error":
        return "エラーが発生しました。";
      default:
        return "";
    }
  }, [phase]);

  const normalizedProgress = useMemo(() => {
    if (typeof progress !== "number" || Number.isNaN(progress)) return null;
    return Math.max(0, Math.min(100, progress));
  }, [progress]);

  const showProgressBar =
    isWorking &&
    !errorText &&
    phase !== "idle" &&
    phase !== "done" &&
    typeof normalizedProgress === "number";

  const handlerClickDownloadButton = useCallback(
    async (e) => {
      e.preventDefault();
      if (isWorking) return;

      setIsWorking(true);
      setPhase("starting");
      setStatusText("build_start.php を呼び出しています。");
      setProgress(null);
      setBuildId("");
      setErrorText("");

      try {
        const meta2 = buildMetaOverride(meta, color1set, shikii1set);

        const [years, yearbeginend] = makeYears(meta2);
        const onlyOneYear = years.filter((y) => y != null && y !== "").length === 1;

        const payload = {
          meta: meta2,
          area,
          ratio,
          alpha,
        };

        console.log("[Csv2kml_v4 payload json]", JSON.stringify(payload));

        // 1. start API
        const startRes = await fetch(BUILD_START_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (!startRes.ok) {
          throw new Error(`build start request failed: ${startRes.status}`);
        }

        const startJson = await startRes.json();

        if (!startJson?.ok) {
          throw new Error(startJson?.message || "build start failed");
        }

        const { id, networkUrl, status } = startJson;

        if (!id) {
          throw new Error("missing build id");
        }

        if (!networkUrl) {
          throw new Error("missing networkUrl");
        }

        setBuildId(id);
        setStatusText(
          status === "success"
            ? "サーバ側のKMLはすでに生成済みです。"
            : "サーバ側でKML生成を開始しました。"
        );

        // 2. 凡例FolderはローカルKMZ側の doc.kml に入れる
        setPhase("building-kmz");
        setStatusText("ローカルKMZを組み立てています。");

        const legendKml = makeLegendFolderKml(meta2, yearbeginend, years.length);

        // 3. root の doc.kml は「凡例 + NetworkLink」
        const rootKml = makeNetworkLinkKml(
          networkUrl,
          `${area}_${meta2.csv_filename}`,
          legendKml
        );

        // 4. 凡例PNGはフロントで生成
        const commonLegend = makeLegendCommonPng(meta2, ratio, onlyOneYear);
        const yearLegendPngs = makeLegendYearPngsForYears(meta2, years, onlyOneYear);

        // 5. KMZ をメモリ上で作成
        const zip = new JSZip();
        zip.file("doc.kml", rootKml);

        const folder = zip.folder("hanrei");
        if (!folder) {
          throw new Error("failed to create hanrei folder");
        }

        folder.file(commonLegend.filename, commonLegend.base64, { base64: true });

        for (const p of yearLegendPngs) {
          folder.file(p.filename, p.base64, { base64: true });
        }

        const kmzBlob = await zip.generateAsync({ type: "blob" });
        const downloadName = `${area}_${meta2.csv_filename}_networklink.kmz`;

        let downloaded = false;
        const downloadOnce = () => {
          if (downloaded) return;
          downloaded = true;
          saveAs(kmzBlob, downloadName);
          setPhase("done");
          setStatusText("KMZのダウンロードを開始しました。");
          setProgress(100);
        };

        // 6. KMZ作成直後に即 status 確認
        setPhase("checking");
        setStatusText("サーバ側の生成状況を確認しています。");

        const firstStatus = await fetchBuildStatus(id);

        const canDownload = (statusJson) => {
          return statusJson.status === "success";
        };

        if (typeof firstStatus.progress === "number") {
          setProgress(firstStatus.progress);
        }

        if (canDownload(firstStatus)) {
          downloadOnce();
          return;
        }

        // 7. 未完了ならポーリング
        setPhase("polling");
        setStatusText("サーバ側のKML生成完了を待っています。");

        const finalStatus = await waitForBuildSuccess(id, {
          timeoutMs: 60000,
          intervalMs: 1000,
          onProgress: (statusJson) => {
            const p =
              typeof statusJson.progress === "number" ? statusJson.progress : null;
            setProgress(p);

            if (statusJson.status === "building") {
              setStatusText("サーバ側のKML生成完了を待っています。");
            }

            if (canDownload(statusJson)) {
              downloadOnce();
            }
          },
        });

        if (canDownload(finalStatus)) {
          downloadOnce();
        }
      } catch (err) {
        console.error("[Csv2kml_v4] download flow failed", err);
        setPhase("error");
        setStatusText("");
        setErrorText(err instanceof Error ? err.message : "unknown error");
      } finally {
        setIsWorking(false);
      }
    },
    [meta, area, ratio, alpha, color1set, shikii1set, isWorking]
  );

  // return (
  //   <div style={{ display: "inline-flex", flexDirection: "column", gap: "6px" }}>
  //     <button onClick={handlerClickDownloadButton} disabled={isWorking}>
  //       {isWorking ? "生成中..." : children}
  //     </button>

  //     {(phase !== "idle" || errorText) && (
  //       <div style={{ fontSize: "12px", lineHeight: 1.5, minWidth: "220px" }}>
  //         {phaseLabel && <div>{phaseLabel}</div>}
  //         {statusText && <div>{statusText}</div>}
  //         {buildId && <div>ID: {buildId}</div>}

  //         {showProgressBar && (
  //           <div
  //             style={{
  //               marginTop: "4px",
  //               height: "2px",
  //               width: "100%",
  //               backgroundColor: "#e5e7eb",
  //               borderRadius: "999px",
  //               overflow: "hidden",
  //             }}
  //           >
  //             <div
  //               style={{
  //                 height: "100%",
  //                 width: `${normalizedProgress}%`,
  //                 backgroundColor: "#9ca3af",
  //                 borderRadius: "999px",
  //                 transition: "width 240ms ease",
  //               }}
  //             />
  //           </div>
  //         )}

  //         {errorText && (
  //           <div style={{ color: "#c62828" }}>
  //             エラー: {errorText}
  //           </div>
  //         )}
  //       </div>
  //     )}
  //   </div>
  // );

  return (
  <div style={{ display: "inline-flex", flexDirection: "column", gap: "6px" }}>
    <button onClick={handlerClickDownloadButton} disabled={isWorking}>
      {isWorking ? "生成中..." : children}
    </button>

    {isWorking && typeof normalizedProgress === "number" && (
      <div
        style={{
          marginTop: "4px",
          height: "2px",
          width: "100%",
          backgroundColor: "#e5e7eb",
          borderRadius: "999px",
          overflow: "hidden",
          minWidth: "180px",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${normalizedProgress}%`,
            backgroundColor: "#9ca3af",
            borderRadius: "999px",
            transition: "width 240ms ease",
          }}
        />
      </div>
    )}

    {errorText && (
      <div style={{ fontSize: "12px", color: "#c62828" }}>
        エラー: {errorText}
      </div>
    )}
  </div>
);

};

export default Csv2kml_v4;